import React from 'react';
import { useData } from '../context/DataContext';
import { PageHeader, MetricCard, StatusBadge, ProgressBar } from '../components/UI';

export default function Dashboard({ setPage }) {
  const { data, activeProject: p } = useData();
  if (!p) return <div className="page-content"><p>No project selected.</p></div>;

  const incidents = data.incidents.filter(i => i.projectId === p.id);
  const mrqs      = data.mrqs.filter(m => m.projectId === p.id);
  const vos       = data.vos.filter(v => v.projectId === p.id);
  const costs     = data.costs.filter(c => c.projectId === p.id);
  const reports   = data.dailyReports.filter(r => r.projectId === p.id);

  const openInc   = incidents.filter(i => i.status === 'Open').length;
  const pendingMRQ= mrqs.filter(m => m.status === 'Pending' || m.status === 'Draft').length;
  const voValue   = vos.reduce((a, v) => a + v.value, 0);
  const totalBudget = costs.reduce((a, c) => a + c.budgeted, 0);
  const totalActual = costs.reduce((a, c) => a + c.actual, 0);
  const spendPct  = totalBudget ? Math.round(totalActual / totalBudget * 100) : 0;

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  const fmtM = (n) => '$' + (n / 1e6).toFixed(2) + 'M';

  return (
    <div className="page-content">
      <PageHeader title={p.name} subtitle={`${p.client} · ${p.start} → ${p.end}`}>
        <StatusBadge value={p.status} />
      </PageHeader>

      {/* KPI Row */}
      <div className="grid-4 mb-20">
        <MetricCard label="Contract Value"  value={fmtM(totalBudget || p.budget)} sub={`Spent: ${fmtM(totalActual || p.spent)}`} />
        <MetricCard label="Overall Progress" value={`${p.progress}%`} sub={
          <div style={{ marginTop: 6 }}><ProgressBar value={p.progress} color="progress-blue" /></div>
        } />
        <MetricCard label="Budget Used" value={`${spendPct}%`}
          color={spendPct > 90 ? 'var(--red)' : spendPct > 75 ? 'var(--amber)' : 'var(--green)'}
          sub={`${fmtM(totalActual)} of ${fmtM(totalBudget)}`} />
        <MetricCard label="Completion Date" value={p.end} sub="Scheduled end" />
      </div>

      {/* Alert row */}
      <div className="grid-3 mb-20">
        <div className="metric-card" style={{ cursor: 'pointer', borderColor: openInc > 0 ? 'var(--red-border)' : undefined }}
          onClick={() => setPage('incidents')}>
          <div className="metric-label">Open Incidents</div>
          <div className="metric-value" style={{ color: openInc > 0 ? 'var(--red)' : 'var(--green)' }}>{openInc}</div>
          <div className="metric-sub">{openInc > 0 ? '⚠ Requires attention' : '✓ All clear'}</div>
        </div>
        <div className="metric-card" style={{ cursor: 'pointer', borderColor: pendingMRQ > 0 ? 'var(--amber-border)' : undefined }}
          onClick={() => setPage('mrq')}>
          <div className="metric-label">Pending MRQs</div>
          <div className="metric-value" style={{ color: pendingMRQ > 0 ? 'var(--amber)' : 'var(--green)' }}>{pendingMRQ}</div>
          <div className="metric-sub">of {mrqs.length} total requisitions</div>
        </div>
        <div className="metric-card" style={{ cursor: 'pointer' }} onClick={() => setPage('vos')}>
          <div className="metric-label">Variation Orders</div>
          <div className="metric-value" style={{ color: 'var(--purple)' }}>{vos.length}</div>
          <div className="metric-sub">Total value: {fmt(voValue)}</div>
        </div>
      </div>

      <div className="grid-2">
        {/* Recent incidents */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Recent Incidents</h3>
            <button className="btn btn-ghost btn-xs" onClick={() => setPage('incidents')}>View all →</button>
          </div>
          {incidents.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No incidents recorded.</p>}
          {incidents.slice(0, 4).map(i => (
            <div key={i.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <span className={`dot ${i.severity === 'High' || i.severity === 'Critical' ? 'dot-red' : i.severity === 'Medium' ? 'dot-amber' : 'dot-gray'}`} style={{ marginTop: 5 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--text-primary)' }} className="truncate">{i.ref} – {i.type}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{i.date} · {i.location}</div>
              </div>
              <StatusBadge value={i.status} />
            </div>
          ))}
        </div>

        {/* Budget by category */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Budget by Category</h3>
            <button className="btn btn-ghost btn-xs" onClick={() => setPage('costs')}>View all →</button>
          </div>
          {costs.slice(0, 6).map(c => {
            const pct = c.budgeted ? Math.round(c.actual / c.budgeted * 100) : 0;
            return (
              <div key={c.id} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: 'var(--text-primary)' }}>{c.category}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{fmt(c.actual)} / {fmt(c.budgeted)}</span>
                </div>
                <ProgressBar value={c.actual} max={c.budgeted} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Last daily report */}
      {reports.length > 0 && (
        <div className="card mt-16">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600 }}>Latest Daily Report</h3>
            <button className="btn btn-ghost btn-xs" onClick={() => setPage('daily')}>View all →</button>
          </div>
          {(() => {
            const r = [...reports].sort((a, b) => b.date.localeCompare(a.date))[0];
            return (
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div className="report-date-box">
                    <div className="report-day">{new Date(r.date + 'T12:00').getDate()}</div>
                    <div className="report-month">{new Date(r.date + 'T12:00').toLocaleDateString('en', { month: 'short' })}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{r.date}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>☁ {r.weather} · WBS {r.wbs}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{r.activities}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 20, marginLeft: 'auto' }}>
                  {[['Labor', r.manpower?.labor], ['Supervisors', r.manpower?.supervisor], ['Engineers', r.manpower?.engineer]].map(([l, v]) => (
                    <div key={l} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{l}</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--blue-text)' }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
